import { useEffect, useState } from 'react';
import { Calendar } from 'lucide-react';
import type { AppConfig } from './config';
import { SearchBar } from './components/SearchBar';
import { EMPTY_FILTERS, FiltersPanel, type FilterValues } from './components/FiltersPanel';
import { ResultsTable, type SortDir, type SortKey } from './components/ResultsTable';
import { ResultsList } from './components/ResultsList';
import { Pagination } from './components/Pagination';
import { DetailDrawer } from './components/DetailDrawer';
import { Disclaimer } from './components/Disclaimer';
import { useRestaurants } from './hooks/useRestaurants';
import { useLookups } from './hooks/useLookups';
import { useGeolocation } from './hooks/useGeolocation';
import { NearbyButton } from './components/NearbyButton';
import type { SearchParams } from './api/client';

interface Props {
  config: AppConfig;
}

const PER_PAGE = 20;

export default function App({ config }: Props) {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortKey>('date_current');
  const [dir, setDir] = useState<SortDir>('desc');
  const [selectedPermit, setSelectedPermit] = useState<string | null>(null);

  const geo = useGeolocation();

  useEffect(() => {
    setPage(1);
  }, [geo.origin]);

  const params: SearchParams = {
    q: query || undefined,
    search_type: filters.search_type,
    hotel: filters.hotel,
    address: filters.address,
    category_id: filters.category_id,
    city_id: filters.city_id,
    zip: filters.zip,
    grade: filters.grade,
    page,
    per_page: PER_PAGE,
    sort,
    dir,
    near_lat: geo.origin?.lat,
    near_lng: geo.origin?.lng,
    near_radius_miles: geo.origin !== null ? config.defaultRadiusMiles : undefined,
  };

  const lookupsQuery = useLookups(config);
  const search = useRestaurants(config, params);

  const onSort = (key: SortKey) => {
    if (key === sort) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(key);
      setDir('asc');
    }
    setPage(1);
  };

  const onSelectPermit = (permit: string) => {
    setSelectedPermit((prev) => (prev === permit ? null : permit));
  };

  const total = search.data?.total ?? 0;
  const usedFallback = search.data?.used_fallback_search === true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 space-y-4">
          <SearchBar
            initial={query}
            onSubmit={(q) => {
              setQuery(q);
              setPage(1);
            }}
          />
          <FiltersPanel
            values={filters}
            lookups={lookupsQuery.data}
            onChange={(v) => {
              setFilters(v);
              setPage(1);
            }}
            onClear={() => {
              setFilters(EMPTY_FILTERS);
              setPage(1);
            }}
            leftAction={
              <NearbyButton
                status={geo.status}
                radiusMiles={config.defaultRadiusMiles}
                error={geo.error}
                onRequest={geo.request}
                onClear={geo.clear}
              />
            }
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <h4 className="text-lg font-semibold text-gray-900">
            {search.isLoading
              ? 'Searching…'
              : `${total.toLocaleString()} ${total === 1 ? 'Result' : 'Results'} Found`}
          </h4>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-600">
            {usedFallback && (
              <span className="inline-flex items-center px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded text-xs">
                Showing common-word fallback matches
              </span>
            )}
            <span className="inline-flex items-center">
              <Calendar className="w-4 h-4 mr-1 text-gray-400" aria-hidden />
              <span className="hidden xs:inline">Sorted by&nbsp;</span>
              {labelFor(sort)} ({dir})
            </span>
            <a
              href={config.complaintUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-snhd-primary hover:bg-snhd-primary-hover rounded-lg transition-colors"
            >
              File a Complaint
            </a>
          </div>
        </div>

        {search.error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            Could not load results: {(search.error as Error).message}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="sm:hidden" data-testid="results-mobile">
            <ResultsList
              results={search.data?.results ?? []}
              onSelect={onSelectPermit}
              selectedPermit={selectedPermit}
              isLoading={search.isLoading}
              overrides={lookupsQuery.data?.overrides ?? []}
              renderDetail={(permit) => <DetailDrawer config={config} permitNumber={permit} />}
            />
          </div>
          <div className="hidden sm:block" data-testid="results-desktop">
            <ResultsTable
              results={search.data?.results ?? []}
              sort={sort}
              dir={dir}
              onSort={onSort}
              onSelect={onSelectPermit}
              selectedPermit={selectedPermit}
              isLoading={search.isLoading}
              overrides={lookupsQuery.data?.overrides ?? []}
              renderDetail={(permit) => <DetailDrawer config={config} permitNumber={permit} />}
            />
          </div>
          <Pagination page={page} perPage={PER_PAGE} total={total} onChange={setPage} />
        </div>

        {config.showDisclaimer && <Disclaimer />}
      </main>
    </div>
  );
}

function labelFor(key: SortKey): string {
  switch (key) {
    case 'restaurant_name':
      return 'Name';
    case 'address':
      return 'Address';
    case 'current_grade':
      return 'Grade';
    case 'date_current':
      return 'Last Inspection';
  }
}
